import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import DataAgreementModal from './DataAgreementModal';
import { redirectToLogin } from './constants/mobileApps';
import { firePAEvent } from './utils/analytics';

/*
 * /researchers — rebuilt 3 Sep 2026 on the landing-page baseline.
 *
 * This was the last page still on the pre-rebuild layout: its own stylesheet,
 * an eyebrow label, a hero that did not use the two-line punchline, and card
 * grids that matched nothing else on the site. Someone arriving here from
 * /cmpx#research would have thought they had left the website.
 *
 * The four capabilities are unchanged in substance. E-consent and screening
 * are now one card, because from a researcher's side they are one thing: the
 * front door onto the study. Bulk export keeps its own section, since it is
 * the only thing on the page gated behind a signed agreement.
 *
 * BlogSignpost is gone. It is a component only this page and /data used, and
 * the baseline signposts with a quiet line of prose instead. Same destination,
 * one less pattern to keep alive.
 */

const ResearchSolutionPage = () => {
    const [showAgreementModal, setShowAgreementModal] = useState(false);

    useEffect(() => {
        document.title = 'For researchers | PhysioActivity';
        const meta = document.querySelector('meta[name="description"]');
        if (meta) meta.setAttribute('content', 'Recruitment, e-consent and continuous wearable data collection for MSK research studies.');
    }, []);

    return (
        <div className="pa2">

            <section className="hero" id="top">
                <div className="wrap two">
                    <div className="heromain">
                        <h1 className="on-dark">
                            <span className="setup">Studies that need wearable data,</span>
                            <span className="payoff">without building the pipeline.</span>
                        </h1>
                        <p className="lede">
                            Recruitment, e-consent, screening and continuous sensor data from
                            participants&rsquo; own phones and wearables, in one platform with
                            audit trails.
                        </p>
                        <p className="nerve">
                            Collected the same way for everyone, which is what makes it comparable
                            across a cohort rather than only within one.
                        </p>
                        <div className="cta-row">
                            <Link
                                className="btn btn-t"
                                to="/contact?who=researcher"
                                onClick={() => firePAEvent('research_cta_click', { source: 'hero' })}
                            >
                                Contact us
                            </Link>
                        </div>
                    </div>

                    <div className="herovis">
                        <div className="dash" aria-label="CMPX researcher view, illustrative">
                            <div className="d-top"><i /><i /><i /><span className="w">CMPX &middot; researcher view</span></div>
                            <div className="d-body">
                                <div className="d-side"><i className="on" /><i /><i /><i /><i /><i /></div>
                                <div className="d-main">
                                    <div className="d-h"><span className="t">Your cohort</span><span className="s">Enrolment and capture</span></div>
                                    <div className="d-tiles">
                                        <div><div className="v">128</div><div className="c">consented</div></div>
                                        <div><div className="v g">96%</div><div className="c">days with data</div></div>
                                        <div><div className="v g">12 wks</div><div className="c">follow-up</div></div>
                                    </div>
                                    <div className="d-chart">
                                        <div className="cl">Data capture, last 8 weeks</div>
                                        <svg viewBox="0 0 320 74" width="100%" height="74" aria-hidden="true">
                                            <polyline points="0,60 45,50 90,46 135,40 180,34 225,30 270,26 320,22" fill="none" stroke="#4CB6C3" strokeWidth="3.4" strokeLinecap="round" />
                                            <polyline points="0,66 45,64 90,65 135,62 180,61 225,60 270,59 320,58" fill="none" stroke="#cbd6e0" strokeWidth="2.2" strokeDasharray="5 5" />
                                        </svg>
                                    </div>
                                    <div className="d-foot"><span>De-identified only</span><span>Export &middot; CSV</span></div>
                                </div>
                            </div>
                        </div>
                        <p className="cap">Illustrative example. Not a real study.</p>
                    </div>
                </div>
            </section>

            <section className="tint" id="what">
                <div className="wrap">
                    <h2>
                        <span className="setup">The infrastructure exists.</span>
                        <span className="payoff">You configure it.</span>
                    </h2>
                    <p className="lede">
                        Recruitment through to follow-up runs on the platform patients already use,
                        so a study does not start with a build.
                    </p>

                    <div className="cards">
                        <div className="card">
                            <h3>Consent and screening</h3>
                            <p>Digital consent with signature capture, secure storage and full audit trails. Inclusion and exclusion criteria you configure, applied automatically.</p>
                        </div>
                        <div className="card">
                            <h3>Continuous collection</h3>
                            <p>Passive sensor data from phones and wearables: accelerometer, gyroscope, heart rate, steps, sleep, anonymised GPS. No diary to keep.</p>
                        </div>
                        <div className="card">
                            <h3>Monitoring</h3>
                            <p>A live view of who is enrolled and whose data is still arriving, so a participant going quiet is visible while the study is running.</p>
                        </div>
                    </div>

                    <p className="quiet">
                        <Link to="/cmpx#roles">What CMPX does</Link> covers the researcher view
                        and where consent sits.{' '}
                        <Link to="/blog/e-consent-and-participant-identity-in-digital-research">
                            E-consent and participant identity
                        </Link>{' '}
                        sets out the two-track identity model and what each implies for ethics
                        approval.
                    </p>
                </div>
            </section>

            <section className="white" id="export">
                <div className="wrap">
                    <h2><span className="setup">Bulk export, under agreement.</span></h2>
                    <p className="lede">
                        For large cohorts, researcher-side bulk export from CMPX can be enabled
                        under a formal data sharing agreement between your institution and
                        PhysioActivity. Request one and a pre-filled agreement is sent for review
                        and digital signature.
                    </p>
                    <div className="cta-row">
                        <button className="btn btn-t" onClick={() => setShowAgreementModal(true)}>
                            Request agreement
                        </button>
                    </div>
                    <p className="quiet">
                        Everything leaving the platform is de-identified, with no route back to an
                        individual and no sharing outside the agreement that authorised it. The{' '}
                        <Link to="/trust-centre">trust centre</Link> covers that boundary in full.
                    </p>
                </div>
            </section>

            <section className="s-promise" id="start">
                <div className="wrap">
                    <h2 className="on-dark">
                        <span className="setup">Tell us what you are measuring.</span>
                        <span className="payoff">We will tell you if it fits.</span>
                    </h2>
                    <p className="lede">
                        Study design, cohort size and what you need to capture. If the platform is
                        wrong for it, we would rather say so early than after ethics approval.
                    </p>
                    <div className="cta-row">
                        <Link
                            className="btn btn-t"
                            to="/contact?who=researcher"
                            onClick={() => firePAEvent('research_cta_click', { source: 'closing' })}
                        >
                            Contact us
                        </Link>
                    </div>
                    <p className="quiet">
                        Already have a CMPX account?{' '}
                        <button className="linklike" onClick={() => redirectToLogin()}>
                            Sign in
                        </button>
                    </p>
                </div>
            </section>

            <DataAgreementModal
                isOpen={showAgreementModal}
                onClose={() => setShowAgreementModal(false)}
            />

        </div>
    );
};

export default ResearchSolutionPage;
